// NOTE: These policies are drafted for production use on the Law Writer platform.
// Have them reviewed by qualified legal counsel for your specific business entity
// before going live. Update the company name, address, and contact details below.

export const policies: Record<string, string> = {
  terms: `TERMS OF SERVICE

Last updated: 8 October 2026

These Terms of Service ("Terms") govern your use of the Law Writer platform ("Platform"), operated by Eneru Technologies ("Company", "we", "us"). By creating an account or using any part of the Platform you agree to be bound by these Terms. If you do not agree, do not use the Platform.

1. DEFINITIONS

"Client" means an individual who registers on the Platform to consult lawyers or receive legal documents.
"Lawyer" means a legal practitioner enrolled with a State Bar Council under the Advocates Act, 1961, who registers on the Platform to offer consultations.
"Consultation" means a time-bound chat or video-call session booked through the Platform between a Client and a Lawyer.
"Document" means any legal draft, template, or file created, generated, or delivered through the Platform.

2. ELIGIBILITY

You must be at least 18 years of age and legally competent to enter into a contract under the Indian Contract Act, 1872. Lawyers must hold a valid enrolment with a State Bar Council and must not be under suspension or debarment.

3. ACCOUNTS AND VERIFICATION

3.1. You must provide accurate information during registration. We verify Lawyer credentials (Bar Council enrolment number) before publishing their profile.
3.2. You are responsible for maintaining the confidentiality of your login credentials. Notify us immediately if you suspect unauthorized access.
3.3. We may suspend or deactivate accounts that provide false information or violate these Terms.

4. PLATFORM SERVICES

4.1. Law Writer provides: (a) AI-powered speech-to-text transcription for legal document creation; (b) pre-built Indian legal document templates; (c) a marketplace connecting Clients with verified Lawyers for paid consultations; (d) AI-assisted document drafting from consultation transcripts.
4.2. The Platform is a technology intermediary. We do not practise law and do not provide legal advice.
4.3. AI-generated content (transcriptions, drafts, translations) is assistive only. Lawyers are responsible for reviewing, editing, and verifying all documents before use.

5. CONSULTATIONS

5.1. Consultations are private sessions between a Client and a Lawyer. The Company is not a party to any professional engagement arising from a consultation.
5.2. Whether a consultation gives rise to an advocate-client relationship is determined solely by the applicable provisions of the Advocates Act, 1961, and Bar Council of India Rules. The Platform does not create such a relationship by itself.
5.3. Lawyers set their own fees. The total fee (including any document add-on) is displayed before booking and processed via Razorpay.
5.4. Chat consultations open 10 minutes before the booked slot and close when the slot ends. Call consultations use a third-party video link.

6. PAYMENTS AND SUBSCRIPTION

6.1. All payments are processed securely through Razorpay and are denominated in Indian Rupees (INR).
6.2. Subscription plans for the document editor: (a) 2-day free trial for new users; (b) Single document credit at ₹10; (c) Monthly plan at ₹499 for unlimited documents.
6.3. Consultation fees are paid directly to secure the booking. The Company facilitates the transaction.
6.4. All prices are inclusive of applicable taxes unless otherwise stated.
6.5. See the Refund & Cancellation Policy for cancellation and refund terms.

7. INTELLECTUAL PROPERTY

7.1. The Platform, its software, design, and branding are the intellectual property of the Company.
7.2. Documents you create or receive through the Platform belong to you. You grant us a limited licence to store and transmit them solely to provide the service.
7.3. Templates provided on the Platform may be used for your legal practice but may not be redistributed or resold.

8. PROHIBITED CONDUCT

You shall not: (a) use the Platform for any unlawful purpose; (b) upload malicious code; (c) impersonate another person or entity; (d) scrape, harvest, or bulk-download Platform content; (e) circumvent payment mechanisms; (f) use AI-generated documents without professional review where court filing or regulatory compliance is involved.

9. LIMITATION OF LIABILITY

9.1. The Platform is provided "as is". To the maximum extent permitted by law, the Company disclaims all warranties, express or implied.
9.2. The Company shall not be liable for: (a) the quality, accuracy, or outcome of legal advice provided by Lawyers; (b) errors in AI-generated transcriptions, drafts, or translations; (c) any indirect, incidental, or consequential damages arising from use of the Platform.
9.3. Our aggregate liability for any claim shall not exceed the amount paid by you to the Company in the 12 months preceding the claim.

10. INDEMNIFICATION

You agree to indemnify the Company against claims, damages, and expenses arising from: (a) your violation of these Terms; (b) your use of documents generated through the Platform; (c) any dispute between a Client and a Lawyer.

11. TERMINATION

11.1. You may delete your account at any time by contacting support.
11.2. We may suspend or terminate accounts for violation of these Terms, with notice where practicable.
11.3. Upon termination, your right to access the Platform ceases. Documents already exported remain with you.

12. GOVERNING LAW AND DISPUTE RESOLUTION

12.1. These Terms are governed by the laws of India.
12.2. Any dispute shall first be attempted to be resolved through mediation. If mediation fails, disputes shall be subject to the exclusive jurisdiction of the courts at New Delhi, India.

13. CHANGES TO TERMS

We may update these Terms from time to time. Material changes will be notified via email or in-app notice. Continued use after the effective date constitutes acceptance.

14. CONTACT

For questions about these Terms, contact us at support@consultwithlawyer.in.`,

  privacy: `PRIVACY POLICY

Last updated: 8 October 2026

This Privacy Policy explains how Eneru Technologies ("Company", "we", "us") collects, uses, stores, and protects your personal data when you use the Law Writer platform ("Platform"). This policy is drafted in accordance with the Digital Personal Data Protection Act, 2023 ("DPDP Act"), the Information Technology Act, 2000, and the Information Technology (Reasonable Security Practices and Procedures and Sensitive Personal Data or Information) Rules, 2011.

1. DATA FIDUCIARY

The Company acts as a Data Fiduciary under the DPDP Act. Our contact details:
Email: privacy@consultwithlawyer.in
Address: [Company registered address]

2. PERSONAL DATA WE COLLECT

2.1. Information you provide:
- Account details: name, email address, mobile number, password (stored as a salted hash)
- Lawyer credentials: Bar Council name, enrolment number, practice areas, experience, fee, professional biography
- Consultation content: topics, booking notes, chat messages, call transcripts, voice recordings (processed for transcription)
- Documents: legal documents you create, edit, or receive
- Payment details: transaction references (actual card/bank details are handled entirely by Razorpay and never stored on our servers)

2.2. Information collected automatically:
- Device and browser information, IP address, access timestamps
- Service usage patterns (pages visited, features used)

3. PURPOSE OF PROCESSING

We process your personal data for the following purposes under the DPDP Act:
- To provide, maintain, and improve the Platform
- To verify Lawyer credentials and manage account approvals
- To process payments through Razorpay
- To send consultation reminders, booking confirmations, and service notifications
- To generate AI-assisted document drafts and translations (using DeepSeek API)
- To transcribe speech to text (using ElevenLabs API)
- To respond to support requests
- To comply with legal obligations

4. CONSENT

4.1. By creating an account, you provide consent for the processing described in this policy.
4.2. You may withdraw consent at any time by contacting us at privacy@consultwithlawyer.in. Withdrawal does not affect the lawfulness of processing done before withdrawal.
4.3. Certain processing is necessary for performing the contract (providing the service) and cannot be opted out of while using the Platform.

5. DATA SHARING

5.1. With Lawyers/Clients: Consultation details, chat messages, and deliverables are shared between the parties to a consultation as necessary to provide the service.
5.2. Payment processor: Razorpay processes payments. Their privacy policy governs payment data: https://razorpay.com/privacy/
5.3. AI service providers: Consultation content may be sent to DeepSeek (for document drafting/translation) and ElevenLabs (for speech transcription). These providers process data under contract and are not permitted to use it for their own purposes.
5.4. Mobile verification: MSG91 processes your mobile number for OTP verification.
5.5. Email service: Resend processes your email address to deliver transactional emails.
5.6. We do not sell your personal data. We do not share personal data for advertising purposes.
5.7. We may disclose data if required by law, court order, or regulatory authority.

6. DATA STORAGE AND SECURITY

6.1. Your data is stored on PostgreSQL databases with encryption in transit (TLS). Passwords are hashed using bcrypt with a cost factor of 12.
6.2. We implement reasonable security practices as required under the IT Act, 2000, and the IT Rules, 2011.
6.3. We use role-based access controls. Admin actions are logged in an audit trail.
6.4. Despite our measures, no electronic transmission or storage is 100% secure. You use the Platform at your own risk.

7. DATA RETENTION

7.1. Account data: retained while your account is active, and for up to 3 years after deletion for legal compliance.
7.2. Consultation transcripts and chat messages: retained for 1 year after the consultation, or longer if required by any pending legal proceeding.
7.3. Payment records: retained for 8 years as required under the Income Tax Act, 1961, and GST regulations.
7.4. Documents: retained while your account is active. Exported documents remain with you.

8. YOUR RIGHTS UNDER THE DPDP ACT

As a Data Principal, you have the right to:
- Access a summary of your personal data and processing activities
- Correct inaccurate or incomplete personal data
- Erase personal data that is no longer necessary (subject to legal retention requirements)
- Nominate another individual to exercise your rights in case of death or incapacity
- Grievance redressal (see Section 11 below)

To exercise these rights, email privacy@consultwithlawyer.in with your registered email address.

9. CHILDREN'S DATA

The Platform is intended for users aged 18 and above. We do not knowingly collect data from children. If we become aware that a minor has provided personal data, we will delete it promptly.

10. CROSS-BORDER DATA TRANSFER

Your data may be processed by AI service providers (DeepSeek, ElevenLabs) whose servers may be located outside India. Such transfers are made in compliance with the DPDP Act and any rules notified thereunder.

11. GRIEVANCE OFFICER

In accordance with the IT Rules, 2011, and the DPDP Act, 2023, our Grievance Officer is:
Name: [Grievance Officer Name]
Email: privacy@consultwithlawyer.in
Response time: We will acknowledge your grievance within 48 hours and resolve it within 30 days.

12. CHANGES TO THIS POLICY

We may update this Privacy Policy to reflect changes in law or our practices. Material changes will be notified via email or in-app notice at least 14 days before they take effect.

13. CONTACT

For privacy-related queries: privacy@consultwithlawyer.in`,

  refunds: `REFUND & CANCELLATION POLICY

Last updated: 8 October 2026

This Refund and Cancellation Policy applies to all paid services on the Law Writer platform operated by Eneru Technologies.

1. CONSULTATION CANCELLATIONS

1.1. Client-initiated cancellation: You may cancel a booked consultation at any time before the scheduled start time through the "Cancel" button on your bookings page.
1.2. Cancellation before the session: If you cancel before the consultation start time, a full refund will be processed to your original payment method.
1.3. No-show: If you do not join the consultation within the scheduled time window, no refund will be issued. The Lawyer's fee is considered earned.
1.4. Lawyer no-show: If the Lawyer fails to join the consultation, you will receive a full refund. Please contact support@consultwithlawyer.in if this occurs.

2. CONSULTATION REFUNDS

2.1. If a consultation is materially disrupted due to a Platform technical failure (not your internet connection), contact support@consultwithlawyer.in within 24 hours for a review. We will issue a refund or credit at our discretion.
2.2. We do not issue refunds for dissatisfaction with the legal advice received. Disputes regarding the quality of legal services are between you and the Lawyer.

3. DOCUMENT SUBSCRIPTION REFUNDS

3.1. Single document credit (₹10): Non-refundable once the credit has been consumed (a document has been created).
3.2. Unused document credits: Refundable within 7 days of purchase if no credit has been consumed. Contact support@consultwithlawyer.in.
3.3. Monthly subscription (₹499): Non-refundable once activated. You will retain access until the end of your billing period.
3.4. Free trial: No payment is involved; no refund applies.

4. HOW REFUNDS ARE PROCESSED

4.1. Eligible refunds are processed through Razorpay to your original payment method.
4.2. Refund processing typically takes 5–10 business days depending on your bank or payment provider.
4.3. You will receive an email confirmation when a refund has been initiated.

5. DISPUTE RESOLUTION

5.1. For any payment dispute, first contact us at support@consultwithlawyer.in with your payment reference and a description of the issue.
5.2. We aim to resolve disputes within 7 business days.
5.3. If we are unable to resolve your concern, you may approach the appropriate Consumer Disputes Redressal Commission under the Consumer Protection Act, 2019.
5.4. For chargebacks initiated through your bank, we reserve the right to provide transaction records to Razorpay for dispute resolution.

6. CONTACT

Email: support@consultwithlawyer.in
Response time: We aim to respond within 2 business days.`,

  disclaimer: `DISCLAIMER

Last updated: 8 October 2026

PLEASE READ THIS DISCLAIMER CAREFULLY BEFORE USING THE LAW WRITER PLATFORM.

1. NO LEGAL ADVICE

The Law Writer platform ("Platform") is a technology tool that assists legal professionals in creating legal documents and connects clients with lawyers. The Platform itself does not provide legal advice, legal opinions, or legal representation.

2. NO ADVOCATE-CLIENT RELATIONSHIP

2.1. Creating an account, browsing lawyer profiles, or using document templates does not establish an advocate-client relationship.
2.2. A consultation booked through the Platform is a private engagement between the Client and the Lawyer. Whether it creates an advocate-client relationship is governed by the Advocates Act, 1961, and the Bar Council of India Rules. The Company is not a party to any such relationship.
2.3. The Company does not endorse, guarantee, or take responsibility for the legal advice, documents, or services provided by any Lawyer on the Platform.

3. AI-GENERATED CONTENT

3.1. The Platform uses artificial intelligence for: (a) speech-to-text transcription; (b) document drafting from consultation transcripts; (c) document translation across Indian languages.
3.2. AI-generated content is assistive and may contain errors, omissions, or inaccuracies. It must be reviewed and verified by a qualified legal professional before use in any legal proceeding, court filing, regulatory submission, or contractual engagement.
3.3. The Company disclaims all liability for consequences arising from the use of AI-generated content without professional review.

4. TEMPLATES

4.1. Legal templates on the Platform are provided as starting points for common Indian legal documents. They are general in nature and may not address the specific requirements of your matter, jurisdiction, or court.
4.2. Templates reflect the law as understood at the time of creation. Laws and court rules change. Users must verify that templates are current and applicable to their case.
4.3. Criminal law templates offer references to both the Indian Penal Code / Code of Criminal Procedure (for cases registered before 1 July 2024) and the Bharatiya Nyaya Sanhita / Bharatiya Nagarik Suraksha Sanhita (for cases registered on or after 1 July 2024). Users must select the correct legal regime.

5. LAWYER VERIFICATION

5.1. We verify that Lawyers registered on the Platform hold a valid Bar Council enrolment number. This verification confirms enrolment; it does not constitute a character reference, competence assessment, or guarantee of the quality of services.
5.2. Ratings and reviews on the Platform are provided by users and reflect individual experiences. They are not professional evaluations.

6. LIMITATION OF LIABILITY

To the maximum extent permitted by law, the Company, its directors, employees, and affiliates shall not be liable for any direct, indirect, incidental, special, or consequential damages arising from: (a) use of or inability to use the Platform; (b) reliance on any content, document, or advice obtained through the Platform; (c) unauthorized access to your data; (d) errors or interruptions in AI services.

7. BAR COUNCIL COMPLIANCE

7.1. Lawyer profiles on the Platform are informational listings maintained by the Lawyers themselves, intended to help clients make informed choices. They are not advertisements within the meaning of Rule 36 of the Bar Council of India Rules.
7.2. Lawyers are responsible for ensuring their profile information complies with Bar Council of India Rules and their respective State Bar Council rules.

8. THIRD-PARTY SERVICES

The Platform integrates with third-party services (Razorpay, ElevenLabs, DeepSeek, MSG91, Jitsi). The Company is not responsible for the availability, accuracy, or security of these services. Their respective terms and privacy policies apply.

9. GOVERNING LAW

This Disclaimer is governed by the laws of India. Any disputes arising from this Disclaimer shall be subject to the exclusive jurisdiction of the courts at New Delhi, India.

10. CONTACT

For questions about this Disclaimer: support@consultwithlawyer.in`,
};

export const policyTitles: Record<string, string> = {
  terms: 'Terms of Service',
  privacy: 'Privacy Policy',
  refunds: 'Refund & Cancellation Policy',
  disclaimer: 'Disclaimer',
};
